import { Button, Input, Table, Tag, Popconfirm } from "antd";
import { Edit, Package, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNotification } from "~/hooks";
import {
  useGetSupplyKits,
  useDeleteSupplyKit,
  SupplyKit,
} from "~/services/supply-kit";
import { useSupplyKitStore } from "./supply-kit.store";
import { ModalSupplyKit } from "./modal-supply-kit";

export const ListSupplyKits = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");

  const { openKitModal } = useSupplyKitStore();
  const { success, error } = useNotification();

  const { data, isLoading } = useGetSupplyKits({
    page,
    pageSize,
    search,
  });

  const deleteMutation = useDeleteSupplyKit();

  const handleDelete = (id: string, name: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        success({
          message: "Kit deleted",
          description: `"${name}" has been removed.`,
        });
      },
      onError: (err) => {
        error({
          message: "Error deleting kit",
          description: err?.message || "An error occurred.",
        });
      },
    });
  };

  const calculateKitTotal = (kit: SupplyKit) => {
    return kit.items.reduce((sum, item) => {
      // Only count billable items in the total
      if (!item.supply?.isBillable) {
        return sum;
      }

      // Use billableRate if set, otherwise use price
      const itemPrice = item.supply.billableRate
        ? Number(item.supply.billableRate)
        : Number(item.supply.price || 0);
      const itemTotal = itemPrice * item.quantity;
      return sum + itemTotal;
    }, 0);
  };

  const columns = [
    {
      title: "Kit Reference",
      dataIndex: "kitRef",
      key: "kitRef",
      render: (ref: string) => <Tag color="blue">{ref}</Tag>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <span className="font-medium">{name}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (desc: string) => (
        <span className="text-gray-600">{desc || "—"}</span>
      ),
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items",
      render: (items: SupplyKit["items"]) => (
        <div>
          <Tag color="green">{items.length} items</Tag>
          {items.length > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              {items.slice(0, 3).map((item) => (
                <div key={item.id}>
                  {item.supply?.name} (×{item.quantity})
                  {item.supply?.isBillable && (
                    <span className="text-blue-600"> • Billable</span>
                  )}
                </div>
              ))}
              {items.length > 3 && (
                <div className="font-medium">+{items.length - 3} more</div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Total Billable",
      key: "total",
      render: (_: unknown, record: SupplyKit) => {
        const total = calculateKitTotal(record);
        return (
          <span className="font-medium text-green-600">
            ${total.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "success" : "default"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: SupplyKit) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<Edit size={16} />}
            onClick={() => openKitModal(record.id)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete kit"
            description={`Are you sure you want to delete "${record.name}"?`}
            onConfirm={() => handleDelete(record.id, record.name)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<Trash2 size={16} />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">
          Manage predefined groups of supplies for quick selection
        </p>
        <Button
          type="primary"
          size="large"
          icon={<Plus size={20} />}
          onClick={() => openKitModal()}
        >
          Create Kit
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <Input.Search
          placeholder="Search kits by name..."
          size="large"
          allowClear
          onSearch={setSearch}
          onChange={(e) => !e.target.value && setSearch("")}
          className="max-w-md"
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <Table
          dataSource={data?.items}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: data?.meta.totalItems,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} kits`,
          }}
        />
      </div>

      <ModalSupplyKit />
    </div>
  );
};
